import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Divider, Modal, Form, DatePicker, Input, List, Tag, Spin, Tabs, Tooltip } from 'antd';
import { ReloadOutlined, CalendarOutlined, DeleteOutlined, EditOutlined, InfoCircleOutlined, LineChartOutlined, RobotOutlined, MessageOutlined, ClockCircleOutlined } from '@ant-design/icons';
import MonthlyMetrics from './MonthlyMetrics';
import DailyMetricsTable from './DailyMetricsTable';
import SummaryMonthlyMetricsTable from './SummaryMonthlyMetricsTable';
import CampaignsList from './CampaignsList';
import MetricChartModal from './MetricChartModal';
import BrandAuditHistoryModal from './BrandAuditHistoryModal';
import BrandHourlyHeatmap from './BrandHourlyHeatmap';
import HourlyMetricsModal from './HourlyMetricsModal';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Dönen Grafik buton stili için CSS
const rotatingGradientStyle = `
  @keyframes rotate {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  .design-btn {
    position: relative;
    background-origin: padding-box, border-box;
    background-clip: padding-box, border-box;
    cursor: pointer;
    color: #191919;
    border: solid 2px transparent;
    background-image: linear-gradient(to right, #fff, #fff), 
                      conic-gradient(#ff7a00, #fff500, #00ff85, #00f0ff, #000aff, #8f00ff, #ff005c, #fe0000);
    border-radius: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 12px;
    margin-left: 8px;
    font-size: 12px;
    font-weight: bold;
    transition: all 0.3s ease;
  }

  .design-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }

  .rotating-gradient {
    background-size: 300% 300%;
    animation: rotate 3s linear infinite;
  }
`;

/**
 * Marka kartı bileşeni
 */
const BrandCard = ({
  brand,
  index,
  allMetricsData,
  handleRecalculateForBrand,
  loading,
  metrics,
  gridData,
  selectedMonth,
  allCampaigns,
  fetchAllCampaigns,
  isEmbedded = false
}) => {
  // State tanımlamaları
  const [campaignModalVisible, setCampaignModalVisible] = useState(false);
  const [campaignForm] = Form.useForm();
  const [editForm] = Form.useForm(); // Düzenleme formu için
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [updatingCampaign, setUpdatingCampaign] = useState(false); // Güncelleme işlemi için
  const [brandCampaigns, setBrandCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [editModalVisible, setEditModalVisible] = useState(false); // Düzenleme modalı için
  const [currentCampaign, setCurrentCampaign] = useState(null); // Şu anda düzenlenen kampanya
  const [chartModalVisible, setChartModalVisible] = useState(false); // Grafik modalı için
  const [auditModalVisible, setAuditModalVisible] = useState(false); // Audit geçmişi modalı için
  const [hourlyModalVisible, setHourlyModalVisible] = useState(false); // Saatlik metrikler modalı için
  const [chartModalInitialTab, setChartModalInitialTab] = useState('chart'); // Grafik modalının başlangıç tab'ı
  
  // Marka para birimi bilgisini al
  const brandCurrency = allMetricsData[brand.id]?.brandInfo?.currency || 'TRY';
  
  // Bileşen mount olduğunda veya campaignModalVisible değiştiğinde markanın kampanyalarını filtrelemek için useEffect
  useEffect(() => {
    if (campaignModalVisible && allCampaigns && allCampaigns.length > 0) {
      setLoadingCampaigns(true);
      // Markanın kampanyalarını filtrele
      const filteredCampaigns = allCampaigns.filter(
        campaign => campaign.Brand && campaign.Brand.id === brand.id
      );
      
      // Kampanyaları başlangıç tarihine göre eskiden yeniye doğru sırala
      const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
        const dateA = new Date(a.startDate);
        const dateB = new Date(b.startDate);
        return dateA - dateB; // Eskiden yeniye doğru sıralama
      });
      
      setBrandCampaigns(sortedCampaigns);
      setLoadingCampaigns(false);
    }
  }, [campaignModalVisible, allCampaigns, brand.id]);
  
  // Kampanya modalını göster
  const showCampaignModal = () => {
    setCampaignModalVisible(true);
    campaignForm.resetFields();
    setActiveTab('1');
  };
  
  // Kampanya ekle modalını kapat
  const handleCampaignCancel = () => {
    setCampaignModalVisible(false);
  };
  
  // Düzenleme modalını kapat
  const handleEditCancel = () => {
    setEditModalVisible(false);
    setCurrentCampaign(null);
  };
  
  // Kampanya düzenleme modalını göster
  const handleEditCampaign = (campaign) => {
    setCurrentCampaign(campaign);
    setEditModalVisible(true);
    
    // Form alanlarını doldur
    editForm.setFieldsValue({
      name: campaign.name,
      dateRange: [
        dayjs(campaign.startDate),
        dayjs(campaign.endDate)
      ],
      description: campaign.description || ''
    });
  };
  
  // Kampanya güncelleme
  const handleUpdateCampaign = async () => {
    if (!currentCampaign) return;
    
    try {
      const values = await editForm.validateFields();
      setUpdatingCampaign(true);
      
      const campaignData = {
        name: values.name,
        startDate: values.dateRange[0].format('YYYY-MM-DD'),
        endDate: values.dateRange[1].format('YYYY-MM-DD'),
        description: values.description
      };
      
      await api.put(`/campaigns/${currentCampaign.id}`, campaignData);
      
      // Başarılı mesajı
      Modal.success({
        title: 'Kampanya Güncellendi',
        content: `"${values.name}" kampanyası başarıyla güncellendi.`
      });
      
      // Kampanyaları yeniden yükle
      fetchAllCampaigns();
      
      // Modalı kapat
      setEditModalVisible(false);
      setCurrentCampaign(null);
      
    } catch (error) {
      // Hata mesajı
      let errorMessage = 'Kampanya güncellenirken bir hata oluştu.';
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      }
      
      Modal.error({
        title: 'Hata',
        content: errorMessage
      });
    } finally {
      setUpdatingCampaign(false);
    }
  };
  
  // Kampanya kaydetme
  const handleSaveCampaign = async () => {
    try {
      const values = await campaignForm.validateFields();
      setSavingCampaign(true);
      
      const campaignData = {
        name: values.name,
        brandId: brand.id,
        startDate: values.dateRange[0].format('YYYY-MM-DD'),
        endDate: values.dateRange[1].format('YYYY-MM-DD'),
        description: values.description
      };
      
      await api.post('/campaigns', campaignData);
      
      // Form sıfırla
      campaignForm.resetFields();
      
      // Başarılı mesajı
      Modal.success({
        title: 'Kampanya Eklendi',
        content: `"${values.name}" kampanyası başarıyla eklendi.`
      });
      
      // Kampanyaları yeniden yükle
      fetchAllCampaigns();
      
      // Liste sekmesine geç
      setActiveTab('2');
      
    } catch (error) {
      // Hata mesajı
      let errorMessage = 'Kampanya eklenirken bir hata oluştu.';
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      }
      
      Modal.error({
        title: 'Hata',
        content: errorMessage
      });
    } finally {
      setSavingCampaign(false);
    }
  };
  
  // Kampanya silme
  const handleDeleteCampaign = async (campaignId, campaignName) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      
      // Başarılı mesajı
      Modal.success({
        title: 'Kampanya Silindi',
        content: `"${campaignName}" kampanyası başarıyla silindi.`
      });
      
      // Kampanya listesini güncelle - silinen kampanyayı kaldır
      setBrandCampaigns(prevCampaigns => 
        prevCampaigns.filter(campaign => campaign.id !== campaignId)
      );
      
      // Kampanyaları yeniden yükle
      fetchAllCampaigns();
    } catch (error) {
      // Hata mesajı
      let errorMessage = 'Kampanya silinirken bir hata oluştu.';
      if (error.response && error.response.data && error.response.data.error) {
        errorMessage = error.response.data.error;
      }
      
      Modal.error({
        title: 'Hata',
        content: errorMessage
      });
    }
  };
  
  // Tarih formatını düzenle
  const formatDate = (dateString) => {
    return dayjs(dateString).format('DD MMM YYYY');
  };
  
  // CSS stillerini sayfaya ekle
  useEffect(() => {
    // Stil elementini oluştur
    const styleElement = document.createElement('style');
    styleElement.appendChild(document.createTextNode(rotatingGradientStyle));
    
    // Head'e ekle
    document.head.appendChild(styleElement);
    
    // Cleanup
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  
  return (
    <Card 
      key={`${brand.id}-${index}`}
      bodyStyle={{ backgroundColor: '' }}
      title={
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full gap-2">
          <div className="flex flex-col">
            <span className="text-sm sm:text-base font-medium truncate">{brand.name}</span>
            <div className="flex flex-wrap gap-x-4 text-xs text-gray-600">
              {brand.advisor && <span>Advisor: {brand.advisor}</span>}
              {brand.growth && <span>Growth: {brand.growth}</span>}
              {brand.assistant && <span>Assistant: {brand.assistant}</span>}
              {brand.meta && <span>Meta: {brand.meta}</span>}
              {brand.google && <span>Google: {brand.google}</span>}
              {brand.seo && <span>SEO: {brand.seo}</span>}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
            <Button
              type="primary"
              size="small"
              icon={<CalendarOutlined />}
              onClick={showCampaignModal}
              className="text-xs sm:text-sm"
              block={window.innerWidth < 640}
            >
              <span className="hidden sm:inline">Kampanyalar</span>
              <span className="sm:hidden">Kampanya</span>
            </Button>
            <Button
              type="default"
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => handleRecalculateForBrand(brand.id, brand.name)}
              loading={loading}
              className="text-xs sm:text-sm"
              block={window.innerWidth < 640}
            >
              <span className="hidden sm:inline">Yeniden Hesapla</span>
              <span className="sm:hidden">Hesapla</span>
            </Button>
          </div>
        </div>
      }
      className="mb-4 shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      <div className="space-y-3 sm:space-y-4">
        <div>
          <Title 
            level={5} 
            className="mb-2 sm:mb-3 text-sm sm:text-base"
          >
            {brand.name} - {selectedMonth.format('MMMM')}
          </Title>
          <MonthlyMetrics 
            brandId={brand.id} 
            allMetricsData={allMetricsData}
          />
        </div>
        
        <Divider className="my-3 sm:my-4" />
        
        <div>
          <Title 
            level={5} 
            className="mb-2 sm:mb-3 text-sm sm:text-base flex flex-col sm:flex-row sm:items-center gap-2"
          >
            <span>{brand.name} - {selectedMonth.format('MMMM')}</span>
            <div className="flex gap-2">
              <button 
                className="design-btn rotating-gradient"
                onClick={() => {
                  setChartModalInitialTab('chart');
                  setChartModalVisible(true);
                }}
                title="Metrik Grafiği Göster"
                style={{ fontSize: window.innerWidth >= 1024 ? '12px' : '9px' }}
              >
                <LineChartOutlined className="mr-1 sm:mr-2" /> 
                <span className="hidden sm:inline">Analyze</span>
                <span className="sm:hidden">Analyze</span>
              </button>
              <button 
                className="design-btn rotating-gradient"
                onClick={() => setAuditModalVisible(true)}
                title="✨ Audit Ai - Yeni audit yap ve geçmişi gör"
                style={{ fontSize: window.innerWidth >= 1024 ? '12px' : '9px' }}
              >
                <RobotOutlined className="mr-1 sm:mr-2" /> 
                <span className="hidden sm:inline">✨ Audit Ai</span>
                <span className="sm:hidden">✨ Audit Ai</span>
              </button>
              <button 
                className="design-btn rotating-gradient"
                onClick={() => {
                  setChartModalInitialTab('askAI');
                  setChartModalVisible(true);
                }}
                title="Ask Ai"
                style={{ fontSize: window.innerWidth >= 1024 ? '12px' : '9px' }}
              >
                <MessageOutlined className="mr-1 sm:mr-2" /> 
                <span className="hidden sm:inline">Ask Ai</span>
                <span className="sm:hidden">Ask Ai</span>
              </button>
              <button 
                className="design-btn rotating-gradient"
                onClick={() => setHourlyModalVisible(true)}
                title="Saatlik Metrik Analizi"
                style={{ fontSize: window.innerWidth >= 1024 ? '12px' : '9px' }}
              >
                <ClockCircleOutlined className="mr-1 sm:mr-2" /> 
                <span className="hidden sm:inline">Hourly</span>
                <span className="sm:hidden">Hourly</span>
              </button>
            </div>
          </Title>
          <DailyMetricsTable 
            brandId={brand.id}
            gridData={gridData}
            metrics={metrics}
            allMetricsData={allMetricsData}
            loading={loading}
          />
          
          {/* Saatlik Isı Haritası */}
          <BrandHourlyHeatmap 
            brandId={brand.id}
            selectedMonth={selectedMonth}
            currency={brandCurrency}
          />
        </div>
        
        {/* Kampanya Listesi */}
        <CampaignsList 
          brandId={brand.id}
          selectedMonth={selectedMonth}
          allCampaigns={allCampaigns}
          onEdit={handleEditCampaign}
        />

        <Divider className="my-3 sm:my-4" />
        
        <div>
          <Title 
            level={5} 
            className="mb-2 sm:mb-3 text-sm sm:text-base"
          >
            {brand.name} - Aylık Özet Metrikler
          </Title>
          <SummaryMonthlyMetricsTable
            brandId={brand.id}
            selectedMonth={selectedMonth}
            loading={loading}
            brandCurrency={brandCurrency}
          />
        </div>
      </div>
      
      {/* Kampanyalar Modal */}
      <Modal
        title={`${brand.name} - Kampanyalar`}
        open={campaignModalVisible}
        onCancel={handleCampaignCancel}
        footer={null}
        bodyStyle={{ padding: 0 }}
        width={window.innerWidth < 768 ? '95vw' : 700}
        style={{ top: window.innerWidth < 768 ? 20 : undefined }}
        destroyOnClose
      >
        <Tabs 
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: '1',
              label: 'Kampanya Ekle',
              children: (
                <Form 
                  form={campaignForm}
                  layout="vertical"
                  onFinish={handleSaveCampaign}
                >
                  <Form.Item
                    name="name"
                    label="Kampanya Adı"
                    rules={[{ required: true, message: 'Lütfen kampanya adını girin' }]}
                  >
                    <Input placeholder="Kampanya adı girin" />
                  </Form.Item>
                  
                  <Form.Item
                    name="dateRange"
                    label="Kampanya Tarihleri"
                    rules={[{ required: true, message: 'Lütfen kampanya tarihlerini seçin' }]}
                  >
                    <RangePicker 
                      style={{ width: '100%' }}
                      format="DD.MM.YYYY"
                      placeholder={['Başlangıç', 'Bitiş']}
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="description"
                    label="Açıklama"
                  >
                    <Input.TextArea />
                  </Form.Item>
                  
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={savingCampaign}
                    style={{ width: '100%' }}
                  >
                    Kampanya Ekle
                  </Button>
                </Form>
              ),
            },
            {
              key: '2',
              label: 'Kampanya Listesi',
              children: (
                <div>
                  {loadingCampaigns ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Spin />
                    </div>
                  ) : (
                    <List
                      dataSource={brandCampaigns}
                      renderItem={campaign => (
                        <List.Item
                          actions={[
                            <Button 
                              type="text" 
                              icon={<EditOutlined />}
                              onClick={() => handleEditCampaign(campaign)}
                            />,
                            <Button 
                              type="text" 
                              danger 
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteCampaign(campaign.id, campaign.name)}
                            />
                          ]}
                        >
                          <List.Item.Meta
                            title={
                              campaign.description ? (
                                <Tooltip title={campaign.description}>
                                  <span>{campaign.name} <InfoCircleOutlined style={{ fontSize: '12px', opacity: 0.6 }} /></span>
                                </Tooltip>
                              ) : (
                                campaign.name
                              )
                            }
                            description={
                              <div>
                                <CalendarOutlined /> {formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}
                              </div>
                            }
                          />
                        </List.Item>
                      )}
                      locale={{ emptyText: 'Kampanya bulunamadı' }}
                    />
                  )}
                </div>
              ),
            },
          ]}
        />
      </Modal>
      
      {/* Kampanya düzenleme modalı */}
      <Modal
        title="Kampanya Düzenle"
        open={editModalVisible}
        onCancel={handleEditCancel}
        confirmLoading={updatingCampaign}
        okText="Güncelle"
        cancelText="İptal"
        onOk={handleUpdateCampaign}
        width={window.innerWidth < 768 ? '95vw' : 520}
        style={{ top: window.innerWidth < 768 ? 20 : undefined }}
        destroyOnClose
      >
        <Form 
          form={editForm}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Kampanya Adı"
            rules={[{ required: true, message: 'Lütfen kampanya adını girin' }]}
          >
            <Input placeholder="Kampanya adı girin" />
          </Form.Item>
          
          <Form.Item
            name="dateRange"
            label="Kampanya Tarihleri"
            rules={[{ required: true, message: 'Lütfen kampanya tarihlerini seçin' }]}
          >
            <RangePicker 
              style={{ width: '100%' }}
              format="DD.MM.YYYY"
              placeholder={['Başlangıç', 'Bitiş']}
            />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="Açıklama"
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* Metrik grafiği modalı */}
      <MetricChartModal
        visible={chartModalVisible}
        onCancel={() => {
          setChartModalVisible(false);
          setChartModalInitialTab('chart'); // Reset to default tab
        }}
        brandId={brand.id}
        brandName={brand.name}
        metrics={metrics}
        allMetricsData={allMetricsData}
        initialTab={chartModalInitialTab}
        allCampaigns={allCampaigns}
      />
      
      {/* Audit geçmişi modalı */}
      <BrandAuditHistoryModal
        visible={auditModalVisible}
        onCancel={() => setAuditModalVisible(false)}
        brand={brand}
      />
      
      {/* Saatlik metrikler modalı */}
      <HourlyMetricsModal
        visible={hourlyModalVisible}
        onCancel={() => setHourlyModalVisible(false)}
        brand={brand}
      />
    </Card>
  );
};

export default BrandCard; 